/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useState } from 'react';

import { css, jsx, keyframes } from '@compiled/react';
import { MDXProvider } from '@mdx-js/react';
import { useLocation } from '@reach/router';
import { bind } from 'bind-event-listener';

import {
	DoDont,
	DoDontGrid,
	Example,
	FeatureFlagOverrides,
	FeatureFlagProvider,
	SideNavigation,
	TopNavigation,
	UserPreferencesProvider,
} from '@af/design-system-docs-ui';
import AppProvider from '@atlaskit/app-provider';
import { getDocument } from '@atlaskit/browser-apis';
import { Code as Snippet } from '@atlaskit/code';
import { cssMap } from '@atlaskit/css';
import { FlagsProvider } from '@atlaskit/flag';
import Link from '@atlaskit/link';
import { Main, Root as PageLayout } from '@atlaskit/navigation-system';

import MetaTags from '../__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/components/meta-tags';
import Props from '../__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/components/props';
import PropsLogger from '../__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/utilities/props-logger';
import { navigation } from '../router/navigation';

import AppProviderRouterLink from './app-provider-router-link';
import Footer from './footer';
import GlobalStyles from './global-styles';
import { HomeSkipLinks } from './home-skip-links';
import { LandingPageSpinner } from './landing-page-spinner';
import SearchModal from './search';
import { SearchModalProvider } from './search/search-modal-context';

type LayoutProps = {
	children: any;
};

const shortcodes = {
	DoDont,
	DoDontGrid,
	Example,
	PropsLogger,
	PropsTable: Props,
	Snippet,
	a: Link,
} as const;

const fadeIn = keyframes({
	from: {
		opacity: 0,
	},
	to: {
		opacity: 1,
	},
});

const homePageContentStyles = css({
	animation: `${fadeIn} 0.3s ease-in-out forwards`,
	opacity: 0,
});

const layoutContainerStyles = css({
	display: 'flex',
	minHeight: '100vh',
	flexDirection: 'column',
	transition: 'background-color 200ms ease',
});

const homeStyles = cssMap({
	layout: {
		width: '100%',
		minHeight: '100vh',
		position: 'relative',
	},
	content: {
		width: '100%',
	},
	contentWrapper: {
		opacity: 0,
		transition: 'opacity 300ms ease-out',
	},
	contentFadeIn: {
		opacity: 1,
	},
});

// Module-level flag - persists across client-side navigation, resets on full page load
let hasShownSpinnerThisSession = false;

/**
 * __Layout__
 *
 * A layout for design system docs.
 *
 */
const Layout = ({ children }: LayoutProps): JSX.Element => {
	const { pathname } = useLocation();
	const currentPath = pathname === '/' || pathname === '' ? '/' : pathname;

	const isHome = currentPath === '/';
	const isNews = pathname.startsWith('/news');
	const showSideNavigation = !(isHome || isNews);

	const [isSearchOpen, setIsSearchOpen] = useState(false);
	const [isSideNavExpanded, setIsSideNavExpanded] = useState(false);
	const [isHomepageLoading, setIsHomepageLoading] = useState(
		() => isHome && !hasShownSpinnerThisSession,
	);
	const [isFadingOut, setIsFadingOut] = useState(false);

	const handleSearchClose = useCallback(() => {
		setIsSearchOpen(false);
	}, []);
	const handleSearchOpen = useCallback(() => {
		setIsSearchOpen(true);
	}, []);

	useEffect(() => {
		// Keyboard shortcut to open search
		return bind(window, {
			type: 'keydown',
			listener: (event: KeyboardEvent) => {
				if (event.metaKey && event.key === 'k' && !event.repeat) {
					setIsSearchOpen(true);
				}
			},
		});
	}, []);

	const handleSideNavExpand = useCallback(
		(_args?: { screen: 'mobile' | 'desktop'; trigger?: unknown }) => {
			setIsSideNavExpanded(true);
		},
		[],
	);

	const handleSideNavCollapse = useCallback(
		(_args?: { screen: 'mobile' | 'desktop'; trigger?: unknown }) => {
			setIsSideNavExpanded(false);
		},
		[],
	);

	// Handle homepage loading state using window.load event
	useEffect(() => {
		if (!isHome) {
			// If initial page load was not homepage, mark session as done
			// so navigating to homepage later won't show the spinner
			hasShownSpinnerThisSession = true;
			setIsHomepageLoading(false);
			setIsFadingOut(false);
			return;
		}

		// Skip spinner on client-side navigation (already shown this session)
		if (hasShownSpinnerThisSession) {
			setIsHomepageLoading(false);
			setIsFadingOut(false);
			return;
		}

		// Show spinner for first full page load
		setIsHomepageLoading(true);
		setIsFadingOut(false);

		const completeLoading = () => {
			if (hasShownSpinnerThisSession) {
				return;
			}
			hasShownSpinnerThisSession = true;
			setIsFadingOut(true);
			// Fade out spinner and background simultaneously, then hide and notify
			setTimeout(() => {
				setIsHomepageLoading(false);
				// Dispatch custom event for components that need to know loading is complete
				if (typeof window !== 'undefined') {
					window.dispatchEvent(new CustomEvent('homepage-loading-complete'));
				}
			}, 300);
		};

		const doc = getDocument();

		// If page is already loaded, complete immediately
		if (doc?.readyState === 'complete') {
			completeLoading();
			return;
		}

		// Wait for window load event (all resources loaded)
		// Only run in browser
		if (typeof window === 'undefined') {
			completeLoading();
			return;
		}

		const unbindLoad = bind(window, { type: 'load', listener: completeLoading });

		// Fallback timeout in case load event doesn't fire (3 seconds max)
		const fallbackTimeout = setTimeout(completeLoading, 3000);

		return () => {
			unbindLoad();
			clearTimeout(fallbackTimeout);
		};
	}, [isHome, pathname]);

	return (
		<MDXProvider components={shortcodes}>
			<AppProvider
				defaultColorMode="light"
				defaultTheme={{
					dark: 'dark',
					light: 'light',
					shape: 'shape',
					spacing: 'spacing',
					typography: 'typography',
					motion: 'motion',
				}}
				routerLinkComponent={AppProviderRouterLink}
			>
				<UserPreferencesProvider>
					<FeatureFlagProvider>
						<FlagsProvider>
							<GlobalStyles />
							<MetaTags isGlobal />
							<SearchModalProvider onOpen={handleSearchOpen} onClose={handleSearchClose}>
								<div css={layoutContainerStyles}>
									{showSideNavigation ? (
										// Standard PageLayout for other pages
										<PageLayout>
											<TopNavigation
												id="top-navigation"
												onSearchOpen={handleSearchOpen}
												pathname={pathname}
												shouldUsePageLayout={true}
												isSideNavExpanded={isSideNavExpanded}
											/>
											{showSideNavigation && (
												<FeatureFlagOverrides
													flagOverrides={{
														'platform-dst-lozenge-tag-badge-visual-uplifts': true,
													}}
												>
													<SideNavigation
														id="side-navigation"
														navigation={navigation}
														currentPath={currentPath}
														onExpand={handleSideNavExpand}
														onCollapse={handleSideNavCollapse}
													/>
												</FeatureFlagOverrides>
											)}
											<Main id="main-content">
												<div key={pathname}>{children}</div>
												<Footer isHome={false} />
											</Main>
										</PageLayout>
									) : (
										// Custom simple layout for homepage
										<div css={homeStyles.layout}>
											<HomeSkipLinks
												links={[
													{ id: 'main-content', label: 'Skip to main content' },
													{ id: 'footer', label: 'Skip to footer' },
												]}
											/>
											<TopNavigation
												id="top-navigation"
												onSearchOpen={handleSearchOpen}
												pathname={pathname}
												isBottomBorderVisible={false}
												shouldUsePageLayout={false}
												isSideNavExpanded={isSideNavExpanded}
											/>
											{isHomepageLoading && <LandingPageSpinner isFadingOut={isFadingOut} />}
											<div
												css={[
													homeStyles.content,
													homeStyles.contentWrapper,
													!isHomepageLoading && homeStyles.contentFadeIn,
												]}
											>
												<div
													key={pathname}
													css={homePageContentStyles}
													role="main"
													id="main-content"
												>
													{children}
												</div>
												{!isHomepageLoading && (
													<div id="footer">
														<Footer isHome={true} />
													</div>
												)}
											</div>
										</div>
									)}
								</div>
								<FeatureFlagOverrides
									flagOverrides={{
										'platform-dst-lozenge-tag-badge-visual-uplifts': true,
									}}
								>
									<SearchModal isOpen={isSearchOpen} onClose={handleSearchClose} />
								</FeatureFlagOverrides>
							</SearchModalProvider>
						</FlagsProvider>
					</FeatureFlagProvider>
				</UserPreferencesProvider>
			</AppProvider>
		</MDXProvider>
	);
};

export default Layout;
